import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import BackdropModal from 'components/portal/backdrop-modal'
import RelatedProductLayer from 'components/shared/product/product-related/related-product-layer'
import { BUTTON_HTML_TYPE } from 'constants/button'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import ResponsiveImage from 'components/images/responsive-image'
import Typo from 'constants/typography'
import Router from 'next/router'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { SEGMENT_REF_TYPES, EVENT } from 'lib/segment/const'
import styles from './slide-content-products.scss'

export const getBannerTaggingProducts = (banner, homefeed) => {
  const bannerProductIds = banner?.relationships?.products?.data?.map(
    (data) => data?.id,
  )

  const bannerProducts = bannerProductIds?.map((id) => {
    const product = homefeed?.included?.find(
      (data) => data?.id === id,
    )?.attributes

    const cleanProduct = {
      ...product,
      id_product: id,
      images: {
        cover_image_path: product?.images?.cover_url,
        hover_image_path: product?.images?.hover_url,
        flatlay_image_path: product?.images?.flatlay_url,
        full_cover_image_path: product?.images?.cover_url,
        full_hover_image_path: product?.images?.hover_url,
        full_flatlay_image_path: product?.images?.flatlay_url,
      },
    }
    return cleanProduct
  })

  return bannerProducts
}

const SlideContentProducts = ({
  t,
  feedId,
  banner,
  isPhone,
  homefeed,
  shopLookCtaEventTracking,
}) => {
  const [isShowNotification, setIsShowNotification] = useState(false)
  const [isOpenGetThisLook, setIsOpenGetThisLook] = useState(false)
  const recommendedProducts = getBannerTaggingProducts(
    banner,
    homefeed?.payload,
  )

  const recommendedProductsCoverImages =
    recommendedProducts?.map(
      (product) => product?.images?.full_cover_image_path,
    ) ?? []

  const maxProductsImages = recommendedProductsCoverImages.slice(0, 3)
  const totalProducts = recommendedProductsCoverImages?.length
  const totalProductsText = hasLocizeTranslation(
    t,
    'NUMBER_ITEMS',
    `(%s items)`,
  ).replace('%s', totalProducts)

  // events
  const handleClickGetThisLook = (event) => {
    event.stopPropagation()
    event.preventDefault()

    setIsOpenGetThisLook(true)

    if (shopLookCtaEventTracking) {
      shopLookCtaEventTracking()
    }
  }

  const handleCloseGetThisLook = (event) => {
    event.stopPropagation()
    event.preventDefault()

    setIsOpenGetThisLook(false)
  }

  // effects
  useEffect(() => {
    const handleRouteChange = () => {
      setIsOpenGetThisLook(false) // Close modal on route change
    }

    Router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      Router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [])

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'RUDDER_UPDATE') return

      const { eventName } = event.data
      const isQuickAddToBag = eventName === EVENT.quickAddToCart
      if (isQuickAddToBag) {
        setIsShowNotification(true)

        setTimeout(() => {
          setIsShowNotification(false)
        }, 3000)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  // Keep the homepage hero focused on its primary CTA on mobile web.
  if (isPhone || !recommendedProducts) return null

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <div
        role="presentation"
        className="slide-content-products__desktop"
        onClick={handleClickGetThisLook}
        onKeyDown={handleClickGetThisLook}
      >
        <div className="slide-content-products__desktop__wrapper">
          <div className="slide-content-products__desktop__img-stack">
            {maxProductsImages.map((image, index) => (
              <ResponsiveImage
                key={`${image}${index}`}
                className="slide-content-products__desktop__img"
                src={image}
                imgRatio={1.33}
                lazy
                lazyThreshold={0.1}
              />
            ))}
          </div>
          <div className="slide-content-products__desktop__content">
            <span
              className={clsx(
                'slide-content-products__desktop__content__title',
                Typo.caption,
              )}
            >
              {hasLocizeTranslation(t, 'GET_THIS_LOOK_TITLE', 'Get this look')}
            </span>
            <span
              className={clsx(
                'slide-content-products__desktop__content__count',
                Typo.price2,
              )}
            >
              {totalProductsText}
            </span>
          </div>
        </div>
      </div>
      <BackdropModal
        zIndex={2000}
        backdropOpacity={0}
        freezeBody={isPhone}
        isOpen={isOpenGetThisLook}
        onBackdropClick={handleCloseGetThisLook}
      >
        <div
          className={clsx(
            `slide-content-products__notification ${
              isShowNotification ? 'show' : ''
            }`,
            Typo.overline,
          )}
        >
          {hasLocizeTranslation(t, 'ADDED_TO_YOUR_BAG', 'Added to your bag')}
        </div>
        <div id="get_this_look" className="slide-content-products__modal">
          {!isPhone && (
            <button
              className="slide-content-products__modal__close-ctn"
              type={BUTTON_HTML_TYPE.button}
              onClick={handleCloseGetThisLook}
            >
              <Icon
                alt="close dialog"
                src={ICONS.close}
                size={ICON_SIZE.medium}
              />
            </button>
          )}
          <RelatedProductLayer
            showTwoPerRow
            productId={feedId}
            products={recommendedProducts}
            title={hasLocizeTranslation(
              t,
              'GET_THIS_LOOK_TITLE',
              'Get this look',
            )}
            refType={SEGMENT_REF_TYPES.APP_FEED}
            elementID="get_this_look"
            displayHoverInteractions
            displayQuickAddToBag
          />
        </div>
      </BackdropModal>
    </React.Fragment>
  )
}

SlideContentProducts.defaultProps = {
  shopLookCtaEventTracking: null,
  homefeed: null,
}

SlideContentProducts.propTypes = {
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  shopLookCtaEventTracking: PropTypes.func,
  feedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  homefeed: PropTypes.shape(),
  banner: PropTypes.shape({
    url: PropTypes.string,
    image: PropTypes.string,
    full_image: PropTypes.string,
    full_image_mobile: PropTypes.string,
    attributes: PropTypes.shape({
      cta: PropTypes.shape({
        kind: PropTypes.string,
        title: PropTypes.string,
        value: PropTypes.string,
      }),
      title: PropTypes.string,
      title_info: PropTypes.shape({}),
      subtitle: PropTypes.string,
      subtitle_info: PropTypes.shape({}),
      media: PropTypes.shape({ kind: PropTypes.string, url: PropTypes.string }),
    }),
    id: PropTypes.string,
  }).isRequired,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
    homefeed: state.homefeed,
  })),
  withI18next(),
)(SlideContentProducts)
